import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import {
  isUserAgentAndroidApp,
} from 'common/util/isUserAgentMobileApp';

import ButtonDeprecated, {
  BUTTON_THEME_BLUE_GREYCLIFF,
} from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrText from 'client/components/SrText';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const DRIVER_CODEBOX_STATE_INITIAL = 'initial';
const DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET = 'serialNumberSet';
const DRIVER_CODEBOX_STATE_MANUAL_INPUT = 'manualInput';
const DRIVER_CODEBOX_STATE_BARCODE_SCANNER = 'barcodeScanner';

const codeboxFormFields = [
  {
    id: 'codeboxSerialNumber',
    type: 'short_text',
    properties: {
      placeholder: 'Write serial code here...',
    },
    validations: ['required'],
  },
];

const SrManualCodeboxForm = ({ onSubmit }) => {
  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({ formFields: codeboxFormFields })}
      validate={createFormValidationFn(codeboxFormFields)}
      render={(formikBag) => (
        <Form>
          {codeboxFormFields.map((formField) => (
            <SrFormikField
              formikBag={formikBag}
              formField={formField}
              theme={SR_FIELD_THEME_ADOBE}
              key={formField.id}
            />
          ))}
          <ButtonDeprecated
            margin='16px 0 0'
            type='submit'
            theme={BUTTON_THEME_BLUE_GREYCLIFF}
          >
            Submit
          </ButtonDeprecated>
        </Form>
      )}
    />
  );
};

SrManualCodeboxForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

SrManualCodeboxForm.displayName = 'SrManualCodeboxForm';

const SrSunSetupDriverJobCodeboxBarcode = ({
  userAgent,
  codeboxLock,
  formikBag,
  onCodeboxBarcodeText,
}) => {
  const [viewState, setViewState] = useState(codeboxLock
    ? DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET
    : DRIVER_CODEBOX_STATE_INITIAL
  );
  const [codeboxMissingError, setCodeboxMissingError] = useState(false);

  const _onCodeboxLock = (codeboxLock) => {
    setCodeboxMissingError(!codeboxLock);
    setViewState(codeboxLock
      ? DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET
      : DRIVER_CODEBOX_STATE_INITIAL
    );
  };

  const _onManualCodebox = ({ codeboxSerialNumber }) => {
    onCodeboxBarcodeText({ codeboxSerialNumber })
      .then((codeboxLock) => {
        return _onCodeboxLock(codeboxLock);

      });
  };

  const _onCodeboxBarcodeText = (barcode) => {
    onCodeboxBarcodeText({ barcode })
      .then((codeboxLock) => {
        return _onCodeboxLock(codeboxLock);
      });
  };

  return (
    <div styleName='card' className='fieldsContainer' id='field-codeboxLockId'>
      <span styleName='cardTitle'>
        Register Codebox
      </span>

      <SrBox margin='16px 0 0'>
        {codeboxMissingError && viewState !== DRIVER_CODEBOX_STATE_MANUAL_INPUT && (
          <SrBox padding='0 0 16px'>
            <SrErrorMessage>
              Codebox not found. Please try again.
            </SrErrorMessage>
          </SrBox>
        )}

        {viewState === DRIVER_CODEBOX_STATE_SERIAL_NUMBER_SET && (
          <div styleName='sunlockBox'>
            <span styleName='title'>Serial Number</span>
            <br />
            <span styleName='serialNumber'>
              {codeboxLock?.serialNumber}
            </span>
            <SrBox margin='16px 0 0'>
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(DRIVER_CODEBOX_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </div>
        )}
        {(
          viewState === DRIVER_CODEBOX_STATE_INITIAL
          || viewState === DRIVER_CODEBOX_STATE_BARCODE_SCANNER
        ) && (
          <>
            {!isUserAgentAndroidApp(userAgent) && (
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(DRIVER_CODEBOX_STATE_BARCODE_SCANNER)}
              >
                Scan Barcode
              </ButtonDeprecated>
            )}
            <SrBox textAlign='center' padding='16px 0 0'>
              <SrText
                font='greycliff'
                weight='800'
                color='blue300'
                decoration='underline'
                fontSize='16px'
                cursor='pointer'
                onClick={() => setViewState(DRIVER_CODEBOX_STATE_MANUAL_INPUT)}
              >
                Manually input Serial Code
              </SrText>
            </SrBox>
          </>
        )}
        {viewState === DRIVER_CODEBOX_STATE_MANUAL_INPUT && (
          <>
            <SrManualCodeboxForm
              onSubmit={_onManualCodebox}
            />
            <SrBox textAlign='center' padding='16px 0 0'>
              <SrText
                font='greycliff'
                weight='800'
                color='blue300'
                decoration='underline'
                fontSize='16px'
                cursor='pointer'
                onClick={() => setViewState(DRIVER_CODEBOX_STATE_INITIAL)}
              >
                Scan Barcode
              </SrText>
            </SrBox>
          </>
        )}
      </SrBox>

      {viewState === DRIVER_CODEBOX_STATE_BARCODE_SCANNER && (
        <SrBarcodeScanner
          onText={_onCodeboxBarcodeText}
          onClose={() => setViewState(DRIVER_CODEBOX_STATE_INITIAL)}
        />
      )}
      <SrFormikErrorMessage
        id='codeboxLockId'
        formikBag={formikBag}
      />
    </div>
  );
};

SrSunSetupDriverJobCodeboxBarcode.propTypes = {
  codeboxLock: PropTypes.object,
  onCodeboxBarcodeText: PropTypes.func.isRequired,
  formikBag: PropTypes.object.isRequired,
};

SrSunSetupDriverJobCodeboxBarcode.displayName = 'SrSunSetupDriverJobCodeboxBarcode';

export default SrSunSetupDriverJobCodeboxBarcode;
